import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals `changeMembershipRole` answers with. The vocabulary covers the eight
 * codes the service can raise, inventoried in the API's `changeMembershipRole` and the
 * `canInviteInto` chain it calls, not only the ones its own `throw` shows. PRIVILEGE_ESCALATION
 * is about the role, not the gesture: the answer is a narrower role, not a right to change roles.
 */
export const changeRoleRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'LAST_ADMINISTRATOR':
            return {
                title: c('Change role refusal').t`This account would be left with nobody to administer it.`,
                guidance: c('Change role refusal')
                    .t`The role you picked does not carry the right to manage users, and this membership is the account's last one that does. Someone must keep it: change another person's role first, or pick a role that keeps user management.`,
            };
        case 'PRIVILEGE_ESCALATION':
            return {
                title: c('Change role refusal').t`You cannot grant this role.`,
                guidance: c('Change role refusal')
                    .t`Its rights reach beyond your own, and nobody grants more than they hold. The refusal is about the role, not about changing it: pick a narrower role, or ask an administrator to widen yours.`,
            };
        case 'ROLE_NOT_FOUND':
            return {
                title: c('Change role refusal').t`This role no longer exists.`,
                guidance: c('Change role refusal')
                    .t`It has left the referential since this page was loaded. Reload the sheet to read the roles as they stand, then pick again.`,
            };
        case 'BACKOFFICE_ROLE_NOT_ALLOWED':
            return {
                title: c('Change role refusal').t`Only portal roles apply here.`,
                guidance: c('Change role refusal')
                    .t`A back-office role cannot be granted on a client account. Pick a portal role for this membership.`,
            };
        case 'MEMBERSHIP_NOT_FOUND':
            return {
                title: c('Change role refusal').t`This access no longer exists.`,
                guidance: c('Change role refusal')
                    .t`It has disappeared since this page was opened. Go back to the list: the sheet you are reading is stale.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Change role refusal').t`You are not allowed to administer this person.`,
                guidance: c('Change role refusal')
                    .t`Changing a role requires the right to manage the users of the account it belongs to, which your role does not carry. Ask for that right, or hand the gesture to whoever administers the account.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Change role refusal').t`This account is outside your portfolio.`,
                guidance: c('Change role refusal')
                    .t`The access belongs to an account that is not entrusted to you. Ask for that account to be added to your portfolio, or hand the gesture over.`,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Change role refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Change role refusal').t`Sign in again, then reopen this sheet to change the role.`,
            };
        default:
            return {
                title: c('Change role refusal').t`The role was not changed.`,
                guidance: c('Change role refusal')
                    .t`This refusal is not one the screen knows how to read. Nothing has changed: the role is still the one it was. The server's answer is below: it is what the support desk needs.`,
                // Kept only when we could not read the refusal.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
